// src/views/Restaurant/ClientManagement/ReservationManagement/components/ClientInfo.tsx
import React, { useEffect, useState, ChangeEvent } from 'react';
import { Button, Form } from 'react-bootstrap';
import 'react-calendar/dist/Calendar.css';
import './ClientInfo.scss';
import { toast } from 'react-toastify';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { createReservation } from '../../../../../../../crud/clients_api/client_reservations';
import { ChevronLeft } from 'react-bootstrap-icons';
import MiniLoadingSpinner from '../../../../../../../components/Spinners/MiniLoadingSpinner';
import { ReservationFormData,ReservationConfirmation } from '../../types';


interface ClientInfoProps {
  formData: ReservationFormData;
  setFormData: any;
  localInfo: any;
  clientLoggedIn: boolean;
  setStage: any;
  setReservationConfirmation: (reservation: ReservationConfirmation) => void;
}

function slotToTime(slot: number | null) {
  if (slot !== null) {
    const hours = Math.floor(slot / 4);
    const minutes = (slot % 4) * 15;
    const ampm = hours < 12 ? 'AM' : 'PM';
    return `${hours % 12 || 12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  }
  return '';
}

function renderDate(date: Date) {
  const days = ['Domingo','Lunes','Martes','Miércoles','Jueves','Viernes','Sábado'];
  const months = [
    'Enero','Febrero','Marzo','Abril','Mayo','Junio',
    'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'
  ];
  return `${days[date.getDay()]} ${date.getDate()} de ${months[date.getMonth()]}`;
}

const ClientInfo: React.FC<ClientInfoProps> = ({
  formData,
  setFormData,
  localInfo,
  clientLoggedIn,
  setStage,
  setReservationConfirmation
}) => {
  const [isFormValid, setIsFormValid] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const userContext = localStorage.getItem('user');
  const userId = userContext ? JSON.parse(userContext).user_id : null;

  // local change handler
  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((fd:any) => ({
      ...fd,
      [name]: value
    }));
  };

  useEffect(() => {
    const client = JSON.parse(localStorage.getItem('client') || '{}');
    if (client.client_id) {
      setIsFormValid(true);
    }
  }, []);

  useEffect(() => {
    const missingFields: string[] = [];
    const client = JSON.parse(localStorage.getItem('client') || '{}');
    if (!client.client_id) {
      if (!formData.fullName) missingFields.push('Nombre y apellido');
      if (!formData.email) missingFields.push('Correo Electrónico');
    }
    if (!formData.phone) missingFields.push('Teléfono');
    setIsFormValid(missingFields.length === 0);
  }, [formData, clientLoggedIn]);

  const handleReserve = () => {
    const missing: string[] = [];
    if (!formData.fullName) missing.push('fullName');
    if (!formData.email) missing.push('email');
    if (!formData.phone) missing.push('phone');

    setMissingFields(missing);

    if (missing.length > 0) {
      setErrorMessage(`Por favor, complete los campos obligatorios.`);
      return;
    }

    setErrorMessage(null);
    setLoading(true);
    createReservation(
      Number(formData.local_id),
      formData.date ? formData.date.toISOString().split('T')[0] : '',
      formData.numPeople,
      formData.selectedSlot,
      formData.mesa_id,
      formData.client_id,
      userId,
      formData.fullName,
      formData.email,
      formData.phone,
      formData.note
    )
      .then((res) => {
        const reservation: ReservationConfirmation = res.data;
        setReservationConfirmation(reservation);
        setStage('success');
        setLoading(false);
      })
      .catch(() => {
        setStage('error');
      });
  };

  const handleGoBack = () => {
    setStage('time');
  };

  const renderReservationSummary = () => (
    <div className="reservation-summary">
      <h5>Resumen de la Reserva</h5>
      <p>
        Reserva para {formData.numPeople}{' '}
        {formData.numPeople <= 1 ? 'persona' : 'personas'},{' '}
        {renderDate(formData.date!)} a las {slotToTime(formData.selectedSlot)}
      </p>
      <p>
        <FaMapMarkerAlt /> {localInfo.address}
      </p>
    </div>
  );

  const renderPersonalInfoForm = () => (
    <>
      <div className="ci-header">
        <Button
          variant="link"
          className="go-back-button"
          onClick={handleGoBack}
        >
          <ChevronLeft /> Volver
        </Button>
        <h4>
          {clientLoggedIn
            ? `Ya casi ${formData.fullName}!`
            : 'Ya casi!'}
        </h4>
      </div>

      <Form className="reservation-personal-info-form">
        {!clientLoggedIn && (
          <Form.Group controlId="formFullName" className='div-reservation-inputs'>
            <Form.Control
              type="text"
              name="fullName"
              className="reservation-inputs"
              placeholder="Ingresa tu nombre y apellido"
              value={formData.fullName}
              onChange={handleChange}
              required
            />
            <span style={{ color: missingFields.includes('fullName') ? 'red' : 'inherit' }}>*</span>
          </Form.Group>
        )}

        <Form.Group controlId="formPhone" className='div-reservation-inputs'>
          <Form.Control
            type="tel"
            name="phone"
            className="reservation-inputs"
            placeholder="Ingresa tu número de teléfono"
            value={formData.phone}
            onChange={handleChange}
            required
          />
          <span style={{ color: missingFields.includes('phone') ? 'red' : 'inherit' }}>*</span>
        </Form.Group>

        <Form.Group controlId="formEmail" className='div-reservation-inputs'>
          <Form.Control
            type="email"
            name="email"
            className="reservation-inputs"
            placeholder="Ingresa tu correo electrónico"
            value={formData.email}
            onChange={handleChange}
            required
          />
          <span style={{ color: missingFields.includes('email') ? 'red' : 'inherit' }}>*</span>
        </Form.Group>

        <Form.Group controlId="formNote">
          <Form.Control
            as="textarea"
            name="note"
            className="reservation-inputs"
            placeholder="¿Alguna solicitud especial?"
            value={formData.note}
            onChange={handleChange}
          />
        </Form.Group>

        {errorMessage && (
          <p style={{ color: 'red', marginBottom: '1rem' }}>{errorMessage}</p>
        )}

        {renderReservationSummary()}

        <Button
          className="final-reserve-button"
          onClick={handleReserve}
          variant="none"
        >
          Confirmar reserva
        </Button>
      </Form>
    </>
  );

  return (
    <>
      {loading ? <MiniLoadingSpinner/> :renderPersonalInfoForm()}
      </>
  );
};

export default ClientInfo;
